// @ts-nocheck -- FormData is deliberately normalised into typed JSON request bodies below.
import { useEffect, useState, type FormEvent } from "react";
import {
  BadgeCheck,
  CheckCircle2,
  CircleDollarSign,
  FileCheck2,
  Plus,
  ReceiptText,
  Scale,
  Users,
  X,
} from "lucide-react";
import { api, useAuth } from "./auth";
type Row = Record<string, any>;
const money = (v: unknown) =>
  new Intl.NumberFormat("en-AE", {
    style: "currency",
    currency: "AED",
    maximumFractionDigits: 2,
  }).format(Number(v || 0));
const label = (v: unknown) =>
  String(v || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());
const emptyDraft = {
  leadId: "",
  propertyId: "",
  dealType: "SALE",
  marketType: "SECONDARY",
  propertyType: "APARTMENT",
  dealValue: "",
  commissionRatePct: "",
  grossCommission: "",
  vatAmount: "0",
  unitNumber: "",
  developerLandlordName: "",
  mouApproverUserId: "",
  mouSigned: false,
  remarks: "",
};
export function CommissionWorkspace() {
  const { csrfToken } = useAuth();
  const [rows, setRows] = useState<Row[]>([]),
    [summary, setSummary] = useState<Row>({}),
    [options, setOptions] = useState<Row>({}),
    [selected, setSelected] = useState<Row | null>(null),
    [detail, setDetail] = useState<Row | null>(null),
    [create, setCreate] = useState(false),
    [error, setError] = useState(""),
    [draft, setDraft] = useState({ ...emptyDraft }),
    [splits, setSplits] = useState([
      { agentUserId: "", splitPct: 50, remarks: "" },
    ]);
  async function load() {
    try {
      const [r, o] = await Promise.all([
        api<{ data: Row[]; summary: Row }>("/api/v1/enterprise/commissions"),
        api<Row>("/api/v1/enterprise/commissions/options"),
      ]);
      setRows(r.data);
      setSummary(r.summary);
      setOptions(o);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Commission workspace could not be loaded",
      );
    }
  }
  useEffect(() => {
    void load();
  }, []);
  async function open(row: Row) {
    setSelected(row);
    setDetail(null);
    setDetail(await api<Row>(`/api/v1/enterprise/commissions/${row.id}`));
  }
  const chosenLead = (options.leads || []).find(
    (x: Row) => x.id === draft.leadId,
  );
  function changeDraft(field: string, value: string | boolean) {
    setDraft((current) => {
      const next = { ...current, [field]: value };
      if (field === "dealValue" || field === "commissionRatePct") {
        const dealValue = Number(
          field === "dealValue" ? value : next.dealValue,
        );
        const rate = Number(
          field === "commissionRatePct" ? value : next.commissionRatePct,
        );
        if (dealValue >= 0 && rate >= 0 && next.commissionRatePct !== "")
          next.grossCommission = String((dealValue * rate) / 100);
      }
      return next;
    });
  }
  function chooseLead(leadId: string) {
    const lead = (options.leads || []).find((x: Row) => x.id === leadId);
    const property = (options.properties || []).find(
      (x: Row) => x.id === lead?.property_id,
    );
    const dealValue = String(
      lead?.deal_value || lead?.asking_price || property?.asking_price || "",
    );
    const rate = String(
      lead?.commission_rate_pct || property?.commission_rate_pct || "",
    );
    const listingType = String(
      lead?.transaction_type || lead?.listing_type || property?.listing_type || "SALE",
    ).toUpperCase();
    setDraft({
      ...emptyDraft,
      leadId,
      propertyId: lead?.property_id || property?.id || "",
      dealType: listingType.includes("RENT") ? "RENTAL" : "SALE",
      marketType:
        String(lead?.market_type || property?.market_type || "SECONDARY").toUpperCase() ===
        "OFF_PLAN"
          ? "OFF_PLAN"
          : "SECONDARY",
      propertyType:
        lead?.linked_property_type ||
        lead?.property_type ||
        property?.property_type ||
        "APARTMENT",
      dealValue,
      commissionRatePct: rate,
      grossCommission:
        dealValue && rate ? String((Number(dealValue) * Number(rate)) / 100) : "",
      vatAmount: "0",
      unitNumber: lead?.unit_number || property?.unit_number || "",
      developerLandlordName:
        lead?.developer_landlord_name || property?.developer_landlord_name || "",
      mouApproverUserId:
        options.settings?.mou_approver_user_id || options.approvers?.[0]?.id || "",
      mouSigned: false,
      remarks: `Created from won lead ${lead?.reference || ""}`.trim(),
    });
    setSplits([
      {
        agentUserId: lead?.assigned_user_id || "",
        splitPct: 50,
        remarks: "Lead owner",
      },
    ]);
  }
  function openCreate() {
    setError("");
    setDraft({
      ...emptyDraft,
      mouApproverUserId:
        options.settings?.mou_approver_user_id || options.approvers?.[0]?.id || "",
    });
    setSplits([{ agentUserId: "", splitPct: 50, remarks: "" }]);
    setCreate(true);
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    try {
      await api("/api/v1/enterprise/commissions", csrfToken, {
        method: "POST",
        body: JSON.stringify({
          leadId: draft.leadId,
          propertyId: draft.propertyId,
          dealType: draft.dealType,
          marketType: draft.marketType,
          propertyType: draft.propertyType,
          dealValue: Number(draft.dealValue),
          commissionRatePct: draft.commissionRatePct
            ? Number(draft.commissionRatePct)
            : null,
          grossCommission: Number(draft.grossCommission),
          vatAmount: Number(draft.vatAmount || 0),
          unitNumber: draft.unitNumber || null,
          developerLandlordName: draft.developerLandlordName || null,
          mouSigned: draft.mouSigned,
          mouApproverUserId: draft.mouApproverUserId,
          remarks: draft.remarks || null,
          splits: splits.map((x) => ({ ...x, splitPct: Number(x.splitPct) })),
        }),
      });
      setCreate(false);
      await load();
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Commission record could not be created",
      );
    }
  }
  return (
    <div className="content commission-workspace">
      <section className="page-heading commission-hero">
        <div>
          <span className="kicker">Restricted finance control</span>
          <h1>Deal commissions</h1>
          <p>
            UAE sales and rental commission entitlement, receipts, approvals and
            agent payments—with every adjustment explained.
          </p>
        </div>
        <button className="button primary" onClick={openCreate}>
          <Plus />
          New closed deal
        </button>
      </section>
      {error && <div className="form-error">{error}</div>}
      <section className="commission-metrics">
        {[
          [CircleDollarSign, summary.gross, "Gross commission", true],
          [
            ReceiptText,
            rows.reduce((n, x) => n + Number(x.amount_received || 0), 0),
            "Received",
            true,
          ],
          [FileCheck2, summary.pending, "Pending controls"],
          [BadgeCheck, summary.payable, "Payable"],
          [Scale, summary.partially_paid, "Partially paid"],
          [CheckCircle2, summary.paid, "Paid"],
        ].map(([Icon, value, title, currency]) => (
          <article key={String(title)}>
            <Icon />
            <span>{title}</span>
            <strong>{currency ? money(value) : value || 0}</strong>
          </article>
        ))}
      </section>
      <section className="panel commission-list">
        <header>
          <div>
            <span className="kicker">Commission register</span>
            <h2>Closed deal control</h2>
          </div>
        </header>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Deal & client</th>
                <th>Type</th>
                <th>Deal value</th>
                <th>Gross</th>
                <th>Net base</th>
                <th>Received</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="clickable-row"
                  onClick={() => void open(row)}
                >
                  <td>
                    <strong>{row.client_name}</strong>
                    <small>
                      {row.reference} · {row.client_mobile || row.client_email}
                    </small>
                  </td>
                  <td>
                    {label(row.deal_type)}
                    <small>
                      {label(row.market_type)} · {label(row.property_type)}
                    </small>
                  </td>
                  <td>{money(row.deal_value)}</td>
                  <td>{money(row.gross_commission)}</td>
                  <td>{money(row.net_commission_base)}</td>
                  <td>{money(row.amount_received)}</td>
                  <td>
                    <span
                      className={`status commission-${String(row.commission_status).toLowerCase()}`}
                    >
                      {label(row.commission_status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      {create && (
        <div className="modal-backdrop">
          <form className="modal commission-create" onSubmit={save}>
            <header>
              <div>
                <span className="kicker">Closed deal intake</span>
                <h2>Create commission record</h2>
              </div>
              <button
                type="button"
                className="icon-button"
                onClick={() => setCreate(false)}
              >
                <X />
              </button>
            </header>
            {chosenLead && (
              <section className="commission-loaded-summary">
                <div>
                  <span>Client</span>
                  <strong>{chosenLead.display_name}</strong>
                  <small>{chosenLead.mobile || chosenLead.email}</small>
                </div>
                <div>
                  <span>Won lead</span>
                  <strong>{chosenLead.reference}</strong>
                  <small>{label(chosenLead.transaction_type || draft.dealType)}</small>
                </div>
                <div>
                  <span>Linked property</span>
                  <strong>{chosenLead.property_title || "Select property"}</strong>
                  <small>{chosenLead.property_reference || chosenLead.unit_number || "Required"}</small>
                </div>
                <div>
                  <span>Lead owner</span>
                  <strong>{(options.agents || []).find((x: Row) => x.id === chosenLead.assigned_user_id)?.display_name || "Select agent"}</strong>
                  <small>Preloaded from allocation history</small>
                </div>
              </section>
            )}
            <div className="form-grid">
              <label>
                Won client / lead
                <select name="leadId" required value={draft.leadId} onChange={(e) => chooseLead(e.target.value)}>
                  <option value="" disabled>
                    Select won lead…
                  </option>
                  {(options.leads || []).map((x: Row) => (
                    <option value={x.id} key={x.id}>
                      {x.reference} · {x.display_name} · {x.mobile || x.email}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Property
                <select name="propertyId" required value={draft.propertyId} onChange={(e) => changeDraft("propertyId", e.target.value)}>
                  <option value="" disabled>Select the transacted property…</option>
                  {(options.properties || []).map((x: Row) => (
                    <option value={x.id} key={x.id}>
                      {x.reference} · {x.title_en}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Deal type
                <select name="dealType" value={draft.dealType} onChange={(e) => changeDraft("dealType", e.target.value)}>
                  <option>SALE</option>
                  <option>RENTAL</option>
                </select>
              </label>
              <label>
                Market
                <select name="marketType" value={draft.marketType} onChange={(e) => changeDraft("marketType", e.target.value)}>
                  <option>SECONDARY</option>
                  <option>OFF_PLAN</option>
                </select>
              </label>
              <label>
                Property type
                <select name="propertyType" value={draft.propertyType} onChange={(e) => changeDraft("propertyType", e.target.value)}>
                  <option>APARTMENT</option>
                  <option>VILLA</option>
                  <option>TOWNHOUSE</option>
                  <option>PENTHOUSE</option>
                  <option>OFFICE</option>
                  <option>RETAIL</option>
                  <option>LAND</option>
                  <option>WAREHOUSE</option>
                </select>
              </label>
              <label>
                Deal value (AED)
                <input
                  name="dealValue"
                  type="number"
                  min="0"
                  step=".01"
                  required
                  value={draft.dealValue}
                  onChange={(e) => changeDraft("dealValue", e.target.value)}
                />
              </label>
              <label>
                Commission rate %
                <input
                  name="commissionRatePct"
                  type="number"
                  min="0"
                  step=".0001"
                  value={draft.commissionRatePct}
                  onChange={(e) => changeDraft("commissionRatePct", e.target.value)}
                />
              </label>
              <label>
                Gross commission (AED)
                <input
                  name="grossCommission"
                  type="number"
                  min="0"
                  step=".01"
                  required
                  value={draft.grossCommission}
                  onChange={(e) => changeDraft("grossCommission", e.target.value)}
                />
              </label>
              <label>
                VAT (AED)
                <input name="vatAmount" type="number" min="0" step=".01" value={draft.vatAmount} onChange={(e) => changeDraft("vatAmount", e.target.value)} />
              </label>
              <label>
                Unit number
                <input name="unitNumber" placeholder="e.g. B1409" value={draft.unitNumber} onChange={(e) => changeDraft("unitNumber", e.target.value)} />
              </label>
              <label>
                Developer / landlord
                <input name="developerLandlordName" value={draft.developerLandlordName} onChange={(e) => changeDraft("developerLandlordName", e.target.value)} />
              </label>
              <label>
                MOU approver
                <select name="mouApproverUserId" required value={draft.mouApproverUserId} onChange={(e) => changeDraft("mouApproverUserId", e.target.value)}>
                  <option value="" disabled>Select approver…</option>
                  {(options.approvers || []).map((x: Row) => (
                    <option value={x.id} key={x.id}>
                      {x.display_name} · {label(x.role)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="check">
                <input name="mouSigned" type="checkbox" checked={draft.mouSigned} onChange={(e) => changeDraft("mouSigned", e.target.checked)} />
                MOU signed
              </label>
              <label className="span-2">
                Deal remarks
                <textarea name="remarks" rows={2} value={draft.remarks} onChange={(e) => changeDraft("remarks", e.target.value)} />
              </label>
            </div>
            <section className="split-editor">
              <header>
                <h3>Flexible agent splits</h3>
                <button
                  type="button"
                  className="button secondary small"
                  onClick={() =>
                    setSplits((x) => [
                      ...x,
                      { agentUserId: "", splitPct: 0, remarks: "" },
                    ])
                  }
                >
                  <Plus />
                  Add agent
                </button>
              </header>
              {splits.map((split, index) => (
                <div key={index}>
                  <select
                    value={split.agentUserId}
                    required
                    onChange={(e) =>
                      setSplits((x) =>
                        x.map((s, i) =>
                          i === index
                            ? { ...s, agentUserId: e.target.value }
                            : s,
                        ),
                      )
                    }
                  >
                    <option value="">Select agent…</option>
                    {(options.agents || []).map((x: Row) => (
                      <option value={x.id} key={x.id}>
                        {x.display_name}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step=".01"
                    value={split.splitPct}
                    onChange={(e) =>
                      setSplits((x) =>
                        x.map((s, i) =>
                          i === index
                            ? { ...s, splitPct: Number(e.target.value) }
                            : s,
                        ),
                      )
                    }
                  />
                  <span>%</span>
                  <input
                    placeholder="Arrangement remarks"
                    value={split.remarks}
                    onChange={(e) =>
                      setSplits((x) =>
                        x.map((s, i) =>
                          i === index ? { ...s, remarks: e.target.value } : s,
                        ),
                      )
                    }
                  />
                  {splits.length > 1 && (
                    <button
                      type="button"
                      onClick={() =>
                        setSplits((x) => x.filter((_, i) => i !== index))
                      }
                    >
                      <X />
                    </button>
                  )}
                </div>
              ))}
            </section>
            <footer>
              <button
                type="button"
                className="button secondary"
                onClick={() => setCreate(false)}
              >
                Cancel
              </button>
              <button className="button primary">
                Create controlled record
              </button>
            </footer>
          </form>
        </div>
      )}
      {selected && (
        <CommissionDrawer
          deal={detail}
          csrfToken={csrfToken}
          onClose={() => {
            setSelected(null);
            setDetail(null);
          }}
          onChanged={() => open(selected).then(load)}
        />
      )}
    </div>
  );
}
function CommissionDrawer({
  deal,
  csrfToken,
  onClose,
  onChanged,
}: {
  deal: Row | null;
  csrfToken: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  if (!deal)
    return (
      <div className="drawer-backdrop">
        <aside className="record-drawer commission-drawer">
          <div className="empty">Loading commission…</div>
        </aside>
      </div>
    );
  const d = deal.data;
  async function submit(path: string, event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const f = new FormData(event.currentTarget),
      body = Object.fromEntries(f);
    for (const key of ["amount"]) if (body[key]) body[key] = Number(body[key]);
    await api(path, csrfToken, { method: "POST", body: JSON.stringify(body) });
    event.currentTarget.reset();
    await onChanged();
  }
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <aside className="record-drawer commission-drawer">
        <header>
          <div>
            <span className="kicker">
              {d.reference} · {label(d.commission_status)}
            </span>
            <h2>{d.client_name}</h2>
            <p>
              {d.client_mobile} · {d.client_email}
            </p>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        <section className="deal-summary">
          <h3>Deal summary</h3>
          <div className="record-facts">
            {[
              ["Deal", `${label(d.deal_type)} · ${label(d.market_type)}`],
              [
                "Property",
                `${label(d.property_type)} · ${d.property_title || d.property_reference || "Not linked"}`,
              ],
              ["Value", money(d.deal_value)],
              ["Unit number", d.unit_number || "Not recorded"],
              [
                "Commission rate",
                d.commission_rate_pct
                  ? `${d.commission_rate_pct}%`
                  : "Entered as amount",
              ],
              [
                "MOU",
                `${d.mou_signed ? "Signed" : "Not signed"} · ${label(d.mou_approval_status)}`,
              ],
              ["Approver", d.mou_approver_name],
            ].map(([a, b]) => (
              <article key={a}>
                <span>{a}</span>
                <strong>{b}</strong>
              </article>
            ))}
          </div>
        </section>
        <section>
          <div className="commission-process">
            {["COMMISSION_ENTERED","RECEIPT_CONFIRMED","SPLIT_CALCULATED","APPROVED","AGENT_PAYMENT","PAID"].map((stage)=><span key={stage} className={stage===d.process_stage?"active":""}>{label(stage)}</span>)}
          </div>
          <a className="button secondary" href={`/api/v1/enterprise/commissions/${d.id}/statement`} target="_blank" rel="noreferrer"><ReceiptText/>Commission statement</a>
        </section>
        <section className="commission-waterfall">
          <h3>Commission waterfall</h3>
          {[
            ["Gross Commission", d.gross_commission],
            ["VAT", d.vat_amount],
            ["Direct Deal Deductions", -Number(d.direct_deductions)],
            ["Net Commission Base", d.net_commission_base],
            [
              "Agent Commission",
              -deal.splits.reduce(
                (n: number, x: Row) => n + Number(x.agent_commission),
                0,
              ),
            ],
            ["RealCo Commission", d.realco_commission],
            ["Received by RealCo", d.amount_received],
          ].map(([name, value]) => (
            <div key={String(name)}>
              <span>{name}</span>
              <strong>{money(value)}</strong>
            </div>
          ))}
        </section>
        <section>
          <h3>Agent entitlement and balance</h3>
          <div className="agent-splits">
            {deal.splits.map((x: Row) => (
              <article key={x.id}>
                <div>
                  <Users />
                  <strong>{x.agent_name}</strong>
                  <small>{x.split_pct}% of net base</small>
                </div>
                <dl>
                  <div>
                    <dt>Total entitlement</dt>
                    <dd>{money(x.agent_commission)}</dd>
                  </div>
                  <div>
                    <dt>Further adjustments</dt>
                    <dd>{money(x.adjustments)}</dd>
                  </div>
                  <div>
                    <dt>Final net payable</dt>
                    <dd>{money(x.final_net_payable)}</dd>
                  </div>
                  <div>
                    <dt>Amount paid</dt>
                    <dd>{money(x.amount_paid)}</dd>
                  </div>
                  <div>
                    <dt>Balance outstanding</dt>
                    <dd>{money(x.balance_outstanding)}</dd>
                  </div>
                </dl>
                {["PAYABLE", "PARTIALLY_PAID", "PAID"].includes(d.commission_status) && (
                  <form
                    onSubmit={(e) =>
                      submit(
                        `/api/v1/enterprise/commission-splits/${x.id}/payments`,
                        e,
                      )
                    }
                  >
                    <input
                      name="amount"
                      type="number"
                      min=".01"
                      step=".01"
                      placeholder="Payment amount"
                      required
                    />
                    <input name="paidAt" type="date" required />
                    <input name="reference" placeholder="Payment reference" />
                    <input
                      name="remarks"
                      placeholder="Mandatory payment remarks"
                      required
                      minLength={3}
                    />
                    <button className="button primary small">
                      Record payment
                    </button>
                  </form>
                )}
              </article>
            ))}
          </div>
        </section>
        <section className="commission-actions">
          <h3>Controls and receipts</h3>
          <div className="control-forms">
            <form
              onSubmit={(e) =>
                submit(`/api/v1/enterprise/commissions/${d.id}/deductions`, e)
              }
            >
              <strong>Direct deduction</strong>
              <select name="category">
                <option>EXTERNAL_BROKER_SHARE</option>
                <option>REFERRAL</option>
                <option>CLIENT_KICKBACK</option>
                <option>MARKETING</option>
                <option>OTHER</option>
              </select>
              <input
                name="amount"
                type="number"
                min=".01"
                step=".01"
                placeholder="Amount"
                required
              />
              <input
                name="remarks"
                placeholder="Mandatory reason"
                required
                minLength={3}
              />
              <button className="button secondary small">Add deduction</button>
            </form>
            <form
              onSubmit={(e) =>
                submit(`/api/v1/enterprise/commissions/${d.id}/receipts`, e)
              }
            >
              <strong>Commission receipt</strong>
              <input
                name="amount"
                type="number"
                min=".01"
                step=".01"
                placeholder="Amount received"
                required
              />
              <input name="receivedAt" type="date" required />
              <input name="reference" placeholder="Bank / receipt reference" />
              <input
                name="remarks"
                placeholder="Mandatory receipt remarks"
                required
                minLength={3}
              />
              <button className="button secondary small">Record receipt</button>
            </form>
          </div>
          <div className="approval-buttons">
            <button
              className="button secondary"
              onClick={() =>
                api(
                  `/api/v1/enterprise/commissions/${d.id}/approve`,
                  csrfToken,
                  {
                    method: "POST",
                    body: JSON.stringify({
                      approval: "MOU",
                      decision: "APPROVED",
                      remarks: "MOU reviewed and approved",
                    }),
                  },
                ).then(onChanged)
              }
            >
              <FileCheck2 />
              Approve MOU
            </button>
            <button
              className="button primary"
              onClick={() =>
                api(
                  `/api/v1/enterprise/commissions/${d.id}/approve`,
                  csrfToken,
                  {
                    method: "POST",
                    body: JSON.stringify({
                      approval: "INTERNAL",
                      decision: "APPROVED",
                      remarks: "Internal deal and commission checks approved",
                    }),
                  },
                ).then(onChanged)
              }
            >
              <BadgeCheck />
              Internal approval
            </button>
          </div>
        </section>
        <section>
          <h3>Linked deal documents</h3>
          <p className="drawer-note">Evidence is linked from the property/deal record, so staff do not upload the same document twice.</p>
          <div className="commission-document-grid">{(deal.documents||[]).map((x:Row)=><article key={x.id}><strong>{x.title}</strong><small>{label(x.document_role)} · {label(x.document_type)}</small></article>)}</div>
          {(deal.availableDocuments||[]).length>0&&<form className="asset-form compact" onSubmit={(e)=>submit(`/api/v1/enterprise/commissions/${d.id}/documents/link`,e)}><label>Existing property document<select name="propertyDocumentId" required><option value="">Select document…</option>{deal.availableDocuments.map((x:Row)=><option value={x.id} key={x.id}>{label(x.document_type)} · {x.title}</option>)}</select></label><label>Commission document role<select name="documentRole"><option>COMMISSION_INVOICE</option><option>PROOF_OF_RECEIPT</option><option>FORM_F_SPA_TENANCY</option><option>FORM_I</option><option>SPLIT_REFERRAL_APPROVAL</option><option>PAYMENT_PROOF</option><option>OTHER</option></select></label><label className="span-2">Remarks<input name="remarks" placeholder="Why this evidence is linked"/></label><button className="button secondary span-2">Link existing document</button></form>}
        </section>
        <section>
          <h3>Receipt history</h3>
          {deal.receipts.map((x: Row) => (
            <p className="drawer-note" key={x.id}>
              {money(x.amount)} ·{" "}
              {new Date(x.received_at).toLocaleDateString("en-AE")} ·{" "}
              {x.reference || "No reference"} · {x.remarks}
            </p>
          ))}
        </section>
      </aside>
    </div>
  );
}
